import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Paper, 
  Chip, 
  Button, 
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Alert,
  Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Beispieldaten für IDs
const sampleIds = [
  { id: 'ID-123', label: 'Blutzucker (mg/dL)', category: 'Labor' },
  { id: 'ID-345', label: 'Blutdruck systolisch (mmHg)', category: 'Vital' },
  { id: 'ID-678', label: 'Cholesterin (mg/dL)', category: 'Labor' },
  { id: 'ID-9', label: 'Herzfrequenz (bpm)', category: 'Vital' },
  { id: 'ID-42', label: 'Alter (Jahre)', category: 'Demographie' },
  { id: 'ID-56', label: 'Gewicht (kg)', category: 'Vital' },
  { id: 'ID-78', label: 'Größe (cm)', category: 'Vital' },
  { id: 'ID-90', label: 'BMI', category: 'Berechnet' },
];

// Operatoren für die Bedingungen
const operators = [
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '>=', label: '>=' },
  { value: '<=', label: '<=' },
  { value: '==', label: '=' },
  { value: '!=', label: '≠' },
];

// Logische Operatoren
const logicalOperators = [
  { value: 'AND', label: 'UND' },
  { value: 'OR', label: 'ODER' },
];

const CohortDefinition = ({ onSaveCohort, savedCohorts = [] }) => {
  const [cohortName, setCohortName] = useState('');
  const [includeConditions, setIncludeConditions] = useState([
    { id: '', operator: '>', value: '', logicalOperator: 'AND' }
  ]);
  const [excludeConditions, setExcludeConditions] = useState([]);
  const [conditionString, setConditionString] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState('');

  // Aktualisiere den Bedingungsstring, wenn sich die Bedingungen ändern
  useEffect(() => {
    try {
      let includeStr = includeConditions
        .filter(cond => cond.id && cond.operator && cond.value)
        .map((cond, index) => {
          const idObj = sampleIds.find(item => item.id === cond.id);
          const idLabel = idObj ? `${cond.id} (${idObj.label})` : cond.id;
          
          let condStr = `${idLabel} ${cond.operator} ${cond.value}`;
          
          // Füge logischen Operator hinzu, wenn nicht der letzte
          if (index < includeConditions.length - 1 && includeConditions[index + 1].id) {
            condStr += ` ${cond.logicalOperator} `;
          }
          
          return condStr;
        })
        .join('');
      
      let excludeStr = excludeConditions
        .filter(cond => cond.id && cond.operator && cond.value)
        .map((cond, index) => {
          const idObj = sampleIds.find(item => item.id === cond.id);
          const idLabel = idObj ? `${cond.id} (${idObj.label})` : cond.id;
          
          let condStr = `${idLabel} ${cond.operator} ${cond.value}`;
          
          // Füge logischen Operator hinzu, wenn nicht der letzte
          if (index < excludeConditions.length - 1 && excludeConditions[index + 1].id) {
            condStr += ` ${cond.logicalOperator} `;
          }
          
          return condStr;
        })
        .join('');
      
      let fullStr = '';
      
      if (includeStr) {
        fullStr += `Include (${includeStr})`;
      }
      
      if (excludeStr) {
        if (includeStr) {
          fullStr += ' AND ';
        }
        fullStr += `Exclude (${excludeStr})`;
      }
      
      setConditionString(fullStr);
      setError('');
    } catch (err) {
      setError('Fehler beim Erstellen des Bedingungsstrings');
    }
  }, [includeConditions, excludeConditions]);

  // Hinzufügen einer neuen Include-Bedingung
  const addIncludeCondition = () => {
    setIncludeConditions([
      ...includeConditions,
      { id: '', operator: '>', value: '', logicalOperator: 'AND' }
    ]);
  };

  // Hinzufügen einer neuen Exclude-Bedingung
  const addExcludeCondition = () => {
    setExcludeConditions([
      ...excludeConditions,
      { id: '', operator: '>', value: '', logicalOperator: 'AND' }
    ]);
  };

  // Aktualisieren einer Include-Bedingung
  const updateIncludeCondition = (index, field, value) => {
    const newConditions = [...includeConditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setIncludeConditions(newConditions);
  };

  // Aktualisieren einer Exclude-Bedingung
  const updateExcludeCondition = (index, field, value) => {
    const newConditions = [...excludeConditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setExcludeConditions(newConditions);
  };

  // Löschen einer Include-Bedingung
  const removeIncludeCondition = (index) => {
    const newConditions = includeConditions.filter((_, i) => i !== index);
    if (newConditions.length === 0) {
      newConditions.push({ id: '', operator: '>', value: '', logicalOperator: 'AND' });
    }
    setIncludeConditions(newConditions);
  };

  // Löschen einer Exclude-Bedingung
  const removeExcludeCondition = (index) => {
    const newConditions = excludeConditions.filter((_, i) => i !== index);
    setExcludeConditions(newConditions);
  };

  // Speichern der Kohorte
  const saveCohort = () => {
    if (!cohortName.trim()) {
      setError('Bitte geben Sie einen Namen für die Kohorte ein');
      return;
    }

    if (includeConditions.length === 0 || !includeConditions[0].id) {
      setError('Bitte fügen Sie mindestens eine Include-Bedingung hinzu');
      return;
    }

    const cohort = {
      name: cohortName,
      includeConditions,
      excludeConditions,
      conditionString
    };

    onSaveCohort(cohort);
    
    // Optional: Zurücksetzen des Formulars
    // setCohortName('');
    // setIncludeConditions([{ id: '', operator: '>', value: '', logicalOperator: 'AND' }]);
    // setExcludeConditions([]);
  };

  // Parsen des Bedingungsstrings (für den fortgeschrittenen Modus)
  const parseConditionString = () => {
    // Hier würde die Logik zum Parsen des Bedingungsstrings implementiert werden
    // Dies ist eine komplexe Aufgabe, die einen Parser erfordern würde
    console.log("Parsing condition string:", conditionString);
    // Für dieses Beispiel tun wir so, als ob es funktioniert hätte
    setError('Diese Funktion ist noch in Entwicklung');
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
        Schritt 1: Kohortendefinition
        <Tooltip title="Definieren Sie die Patientenkohorte durch Ein- und Ausschlusskriterien">
          <IconButton size="small">
            <HelpOutlineIcon fontSize="small" color="primary" />
          </IconButton>
        </Tooltip>
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Spezifizieren Sie, welche Daten in Ihre Analyse einbezogen werden sollen, basierend auf Ein- und Ausschlusskriterien.
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Kohortenname
              </Typography>
              <TextField
                fullWidth
                label="Name der Kohorte"
                variant="outlined"
                value={cohortName}
                onChange={(e) => setCohortName(e.target.value)}
                placeholder="z.B. Diabetes Typ 2 Patienten"
                sx={{ mb: 2 }}
              />
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                Einschlusskriterien
                <Tooltip title="Patienten, die diese Kriterien erfüllen, werden in die Kohorte aufgenommen">
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              </Typography>
              
              {includeConditions.map((condition, index) => (
                <Box key={`include-${index}`} sx={{ mb: 2, p: 2, bgcolor: 'rgba(255, 122, 0, 0.03)', borderRadius: 1, border: '1px solid rgba(255, 122, 0, 0.1)' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <Autocomplete
                        options={sampleIds}
                        getOptionLabel={(option) => typeof option === 'string' ? option : `${option.id} (${option.label})`}
                        renderInput={(params) => <TextField {...params} label="Variable" variant="outlined" size="small" />}
                        value={sampleIds.find(item => item.id === condition.id) || null}
                        onChange={(_, newValue) => updateIncludeCondition(index, 'id', newValue ? newValue.id : '')}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Operator</InputLabel>
                        <Select
                          value={condition.operator}
                          label="Operator"
                          onChange={(e) => updateIncludeCondition(index, 'operator', e.target.value)}
                        >
                          {operators.map((op) => (
                            <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        fullWidth
                        label="Wert"
                        variant="outlined"
                        size="small"
                        value={condition.value}
                        onChange={(e) => updateIncludeCondition(index, 'value', e.target.value)}
                        type="number"
                      />
                    </Grid>
                    {index < includeConditions.length - 1 && (
                      <Grid item xs={6} sm={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Verknüpfung</InputLabel>
                          <Select
                            value={condition.logicalOperator}
                            label="Verknüpfung"
                            onChange={(e) => updateIncludeCondition(index, 'logicalOperator', e.target.value)}
                          >
                            {logicalOperators.map((op) => (
                              <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    <Grid item xs={6} sm={2}>
                      <IconButton 
                        color="error" 
                        onClick={() => removeIncludeCondition(index)}
                        disabled={includeConditions.length === 1 && index === 0}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}
              
              <Button 
                startIcon={<AddIcon />} 
                onClick={addIncludeCondition}
                variant="outlined"
                color="primary"
                sx={{ mb: 3 }}
              >
                Einschlusskriterium hinzufügen
              </Button>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                Ausschlusskriterien (optional)
                <Tooltip title="Patienten, die diese Kriterien erfüllen, werden aus der Kohorte ausgeschlossen">
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              </Typography>
              
              {excludeConditions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                  Keine Ausschlusskriterien definiert
                </Typography>
              ) : (
                excludeConditions.map((condition, index) => (
                  <Box key={`exclude-${index}`} sx={{ mb: 2, p: 2, bgcolor: 'rgba(239, 68, 68, 0.03)', borderRadius: 1, border: '1px solid rgba(239, 68, 68, 0.1)' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <Autocomplete
                          options={sampleIds}
                          getOptionLabel={(option) => typeof option === 'string' ? option : `${option.id} (${option.label})`}
                          renderInput={(params) => <TextField {...params} label="Variable" variant="outlined" size="small" />}
                          value={sampleIds.find(item => item.id === condition.id) || null}
                          onChange={(_, newValue) => updateExcludeCondition(index, 'id', newValue ? newValue.id : '')}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Operator</InputLabel>
                          <Select
                            value={condition.operator}
                            label="Operator"
                            onChange={(e) => updateExcludeCondition(index, 'operator', e.target.value)}
                          >
                            {operators.map((op) => (
                              <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField
                          fullWidth
                          label="Wert"
                          variant="outlined"
                          size="small"
                          value={condition.value}
                          onChange={(e) => updateExcludeCondition(index, 'value', e.target.value)}
                          type="number"
                        />
                      </Grid>
                      {index < excludeConditions.length - 1 && (
                        <Grid item xs={6} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Verknüpfung</InputLabel>
                            <Select
                              value={condition.logicalOperator}
                              label="Verknüpfung"
                              onChange={(e) => updateExcludeCondition(index, 'logicalOperator', e.target.value)}
                            >
                              {logicalOperators.map((op) => (
                                <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                      <Grid item xs={6} sm={2}>
                        <IconButton color="error" onClick={() => removeExcludeCondition(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))
              )}
              
              <Button 
                startIcon={<AddIcon />} 
                onClick={addExcludeCondition}
                variant="outlined"
                color="error"
                sx={{ mb: 3 }}
              >
                Ausschlusskriterium hinzufügen
              </Button>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Zusammenfassung der Kohortendefinition
              </Typography>
              
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  bgcolor: 'background.default',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {conditionString ? (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {conditionString}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Definieren Sie mindestens ein Einschlusskriterium, um die Zusammenfassung zu sehen
                  </Typography>
                )}
              </Paper>
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  {showAdvanced ? 'Einfachen Modus anzeigen' : 'Erweiterten Modus anzeigen'}
                </Button>
                
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={saveCohort}
                  disabled={!cohortName || includeConditions.length === 0 || !includeConditions[0].id}
                >
                  Kohorte speichern
                </Button>
              </Box>
              
              {showAdvanced && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Erweiterte Bedingungsdefinition
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Bedingungsstring"
                    variant="outlined"
                    value={conditionString}
                    onChange={(e) => setConditionString(e.target.value)}
                    placeholder="Include (ID-123 > 4.5 AND ID-345 > 3.4) AND Exclude (ID-678 > 6.5)"
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={parseConditionString}
                  >
                    Bedingungsstring parsen
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Gespeicherte Kohorten
              </Typography>
              
              {savedCohorts.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Keine gespeicherten Kohorten vorhanden
                </Typography>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {savedCohorts.map((cohort, index) => (
                    <Chip
                      key={index}
                      label={cohort.name}
                      color="primary"
                      variant="outlined"
                      onClick={() => {
                        setCohortName(cohort.name);
                        setIncludeConditions(cohort.includeConditions);
                        setExcludeConditions(cohort.excludeConditions);
                      }}
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
              )}
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Hilfe & Tipps
              </Typography>
              
              <Typography variant="body2" paragraph>
                <strong>Einschlusskriterien:</strong> Definieren Sie, welche Patienten in Ihre Kohorte aufgenommen werden sollen.
              </Typography>
              
              <Typography variant="body2" paragraph>
                <strong>Ausschlusskriterien:</strong> Definieren Sie, welche Patienten aus Ihrer Kohorte ausgeschlossen werden sollen.
              </Typography>
              
              <Typography variant="body2" paragraph>
                <strong>Beispiel:</strong> Um Diabetes-Typ-2-Patienten zu definieren, könnten Sie einschließen: &quot;Blutzucker (ID-123) &gt; 126 mg/dL&quot; und ausschließen: &quot;Alter (ID-42) &lt; 18&quot;.
              </Typography>
              
              <Typography variant="body2">
                <strong>Tipp:</strong> Speichern Sie Ihre Kohortendefinitionen, um sie für zukünftige Anfragen wiederzuverwenden.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CohortDefinition;
